import { formatMoney, formatWeekRange, toDateTime, } from '@tymia/shared';
import { DateTime, } from 'luxon';
import { isIncludedIn, } from 'remeda';
import {} from '../user/UserApi.js';
export const deDE = {
    name: 'Deutsch',
    language: 'de',
    locale: 'de-DE',
    region: null,
    dir: 'ltr',
    collator: new Intl.Collator('de-DE'),
    urls: [
        ['/search/psychotherapists-in-{-$cityslug}', '/suche/psychotherapeuten-in-{-$cityslug}'],
        ['/profile/$customerId/$userId/$', '/profil/$customerId/$userId/$'],
    ],
    common: {
        paging: {
            prev: 'vorherige Seite',
            next: 'nächste Seite',
            noSearchResults: 'Leider konnten wir keine passenden Ergebnisse zu Ihrer Suche finden.',
        },
        button: {
            search: 'Suchen',
            next: 'Weiter',
        },
        error: {
            network: 'Es ist ein Netzwerkfehler aufgetreten. Bitte versuchen Sie es später erneut.',
            isRequired: 'Dies ist ein Pflichtfeld.',
        },
        term: {
            Yes: 'Ja',
            No: 'Nein',
        },
        validation(error, _context) {
            if (error.keyword === 'type') {
                if (error.params.type === 'string') {
                    return 'Dies ist ein Pflichtfeld.';
                }
            }
            if (error.keyword === 'enum') {
                if (error.params.allowedValues) {
                    // the value is not one of the allowed values,
                    // but the only case is if the value is missing
                    return 'Dies ist ein Pflichtfeld.';
                }
            }
            if (error.keyword === 'format') {
                if (error.params.format === 'idn-email') {
                    return 'Es muss eine gültige E-Mail-Adresse eingegeben werden.';
                }
            }
            if (error.keyword === 'pattern') {
                if (error.params.pattern === '^[0-9]+$') {
                    return 'Es dürfen nur Zahlen eingegeben werden.';
                }
            }
            return error.message ?? 'Ungültiger Wert';
        },
        date(date) {
            const dateTime = toDateTime(date);
            return dateTime.toFormat('dd.LL.y');
        },
        /** enforce time in a particular timezone */
        dateTz(date, zone = 'Europe/Berlin') {
            const dateTime = toDateTime(date);
            return deDE.common.date(dateTime.setZone(zone));
        },
        dateTime(date) {
            const dateTime = toDateTime(date);
            return dateTime.toFormat('dd.LL.y HH:mm');
        },
        time(date) {
            const dateTime = toDateTime(date).setLocale(deDE.locale);
            return dateTime.toLocaleString(DateTime.TIME_SIMPLE);
        },
        formatWeekRange(start, end) {
            return formatWeekRange(start, end, deDE.locale);
        },
        money(value) {
            return formatMoney(value, 'de-DE');
        },
    },
    user: {
        PatientNumber: 'Patientennummer',
        Salutation: 'Anrede',
        salutationLabel(salutation) {
            return {
                'Mr.': 'Herr',
                'Ms.': 'Frau',
                Diverse: 'Divers',
            }[salutation];
        },
        Title: 'Titel',
        FirstName: 'Vorname',
        LastName: 'Nachname',
        Password: 'Passwort',
        EMail: 'E-Mail Adresse',
        PhoneCountry: 'Landesvorwahl',
        PhoneMobile: 'Mobilnummer',
        Website: 'Webseite',
        InsuranceType: 'Versicherungsart',
        insuranceTypeLabel(type) {
            return {
                legally: 'Gesetzlich versichert',
                privately: 'Privat versichert / Selbstzahler',
            }[type];
        },
        Birthday: 'Geburtstag',
        CareOf: 'c/o',
        Street: 'Straße, Hausnummer',
        PostalCode: 'Postleitzahl',
        City: 'Stadt',
        Country: 'Land (Postadresse)',
        InvoicePostalOnly: 'Rechnungen ausschließlich per Post gewünscht',
        InvoiceAddress: 'Abweichende Rechnungsadresse',
        InternalNote: 'Notizen',
        InvoiceRemark: 'Bemerkung auf der Rechnung',
        name(user) {
            return [user.title, user.firstName, user.lastName].filter(Boolean).join(' ');
        },
        salutationFullName(user) {
            const salutation = isIncludedIn(user.salutation, ['Mr.', 'Ms.'])
                ? user.salutation
                : null;
            return [
                {
                    'Mr.': 'Lieber Herr',
                    'Ms.': 'Liebe Frau',
                    Other: 'Guten Tag',
                }[salutation ?? 'Other'],
                user.title,
                ...salutation ? [] : [user.firstName],
                user.lastName,
            ]
                .filter(Boolean)
                .join(' ');
        },
        JobTitle: 'Berufsgruppe',
        jobTitles: {
            'Ms.': {
                none: '',
                psychologicalPsychotherapist: 'Psychologische Psychotherapeutin',
                psychologicalPsychotherapistChildren: 'Psychologische Psychotherapeutin für Kinder und Jugendliche',
                specialistPsychiatryPsychotherapy: 'Fachärztin für Psychiatrie und Psychotherapie',
                specialistPsychiatryPsychotherapyChildren: 'Fachärztin für Kinder- und Jugendlichenpsychiatrie und -psychotherapie',
                specialistPsychosomatics: 'Fachärztin für Psychosomatische Medizin und Psychotherapie',
                physicianAdditionalQualificationPsychotherapy: 'Ärztin mit Zusatzbezeichnung Psychotherapie',
                nonMedicalPractitionerPsychotherapy: 'Heilpraktikerin für Psychotherapie',
                socialPedagogue: 'Sozialpädagogin',
                coachOrConsultant: 'Coach / Beraterin',
            },
            Other: {
                none: '',
                psychologicalPsychotherapist: 'Psychologischer Psychotherapeut',
                psychologicalPsychotherapistChildren: 'Psychologischer Psychotherapeut für Kinder und Jugendliche',
                specialistPsychiatryPsychotherapy: 'Facharzt für Psychiatrie und Psychotherapie',
                specialistPsychiatryPsychotherapyChildren: 'Facharzt für Kinder- und Jugendlichenpsychiatrie und -psychotherapie',
                specialistPsychosomatics: 'Facharzt für Psychosomatische Medizin und Psychotherapie',
                physicianAdditionalQualificationPsychotherapy: 'Arzt mit Zusatzbezeichnung Psychotherapie',
                nonMedicalPractitionerPsychotherapy: 'Heilpraktiker für Psychotherapie',
                socialPedagogue: 'Sozialpädagoge',
                coachOrConsultant: 'Coach / Berater',
            },
        },
        jobTitle(data) {
            return data.jobTitleFreetext
                ?? this.jobTitles[data.salutation === 'Ms.' ? 'Ms.' : 'Other'][data.jobTitleType ?? 'none'];
        },
        TherapeuticFocus: 'Therapieschwerpunkte',
        TherapeuticFocusShort: 'Schwerpunkte',
        therapeuticFocusNames() {
            return {
                ADHD: [
                    'ADHS',
                    'ADS',
                    'Aufmerksamkeitsdefizit- / Hyperaktivitätsstörung',
                    'Aufmerksamkeitsdefizit-Hyperaktivitätsstörung',
                ],
                'Anxiety and Panic Disorders': ['Angst- und Panikstörungen', 'Panikstörung', 'Panikattacken', 'Phobien'],
                'Adjustment Disorder': ['Anpassungsstörung', 'Anpassungsstörung und Lebenskrisen', 'Lebenskrise'],
                'Bipolar Disorder': ['Bipolare Störung'],
                Burnout: ['Burnout', '„Burnout“ und stressassoziierte Erkrankungen', 'Erschöpfung'],
                'Social Phobia': ['Soziale Phobie'],
                'Generalized Anxiety Disorder': ['Generalisierte Angststörung'],
                Agoraphobia: ['Agoraphobie'],
                Depression: [
                    'Depression',
                    'Depressionen',
                    'depressive Entwicklung',
                    'depressive Verstimmung',
                    'Niedergeschlagenheit',
                ],
                'Borderline Disorder': ['Borderline-Störung', 'Emotionale Instabilität', 'Borderline', 'emotional instabil'],
                'Eating Disorders': ['Essstörung', 'Anorexie', 'Bulimie', 'Binge'],
                'Family Therapy': ['Familientherapie'],
                'Group Therapy': ['Gruppentherapie'],
                'Couples Therapy': ['Paartherapie', 'Paargespräche'],
                'Personality Disorders': ['Persönlichkeitsstörungen', 'Persönlichkeitsstörung'],
                'Post-Traumatic Stress Disorder': ['Posttraumatische Belastungsstörung', 'Trauma', 'PTBS'],
                'Psycho-Oncology': ['Psychoonkologie'],
                Psychocardiology: ['Psychokardiologie'],
                'Psychosis - Schizophrenia': ['Psychose - Schizophrenie'],
                'Psychosomatic Disorders': ['Psychosomatische Beschwerden', 'somatoform', 'psychosomatisch'],
                Pain: ['Schmerzen'],
                Sexuality: ['Sexualität'],
                Addiction: ['Sucht', 'Alkoholabhängigkeit', 'Drogen'],
                'Child and Adolescent Therapy': ['Therapie für Kinder und Jugendliche', 'Jugendliche'],
                Grief: ['Trauer'],
                'Obsessive-Compulsive Disorder': ['Zwangsstörung', 'Zwang', 'Zwangsgedanken', 'Zwangshandlungen'],
            };
        },
        therapeuticFocusLabel(focus) {
            const value = deDE.user.therapeuticFocusNames()[focus];
            return value?.[0] ?? focus;
        },
        TreatmentMethods: 'Verfahren',
        treatmentMethodsNames() {
            return {
                'Behavioral Therapy': ['Verhaltenstherapie', 'kognitive Verhaltenstherapie', 'KVT'],
                'Psychodynamic Therapy': ['Tiefenpsychologisch fundierte Psychotherapie', 'tiefenpsychologische Therapie'],
                Coaching: ['Coaching'],
                'Systemic Therapy': ['Systemische Therapie'],
                'Psychological Counseling': ['Psychologische Beratung'],
                'Schema Therapy': ['Schematherapie'],
                'Relaxation Techniques': [
                    'Entspannungsverfahren',
                    'Autogenes Training',
                    'PMR',
                    'Yoga',
                    'Meditation',
                    'Tai-Chi',
                    'Qigong',
                    'progressive Muskelentspannung nach Jacobson',
                ],
                'Dialectical Behavior Therapy': [
                    'Dialektisch-behaviorale Therapie (DBT)',
                    'DBT',
                    'Dialektisch-behaviorale Therapie',
                ],
                'Person-Centered Therapy': ['Gesprächstherapie'],
                Hypnotherapy: ['Hypnotherapie', 'Hypnose'],
                'Integrative Therapy': ['Integrative Therapie'],
                'Somatic Therapy': ['Körperorientierte Verfahren'],
                EMDR: [
                    'EMDR',
                    'Eye Movement Desensitization and Reprocessing',
                    'Desensibilisierung und Verarbeitung durch Augenbewegung',
                ],
                'Humanistic Therapy': ['Humanistische Verfahren'],
                'Art Therapy': ['Kunsttherapie'],
                Psychoanalysis: ['Psychoanalyse', 'analytische Therapie'],
                Supervision: ['Supervision'],
            };
        },
        treatmentMethodLabel(method) {
            const value = deDE.user.treatmentMethodsNames()[method];
            return value?.[0] ?? method;
        },
        CoverageType: 'Abrechnung',
        coverageTypeLabel(type) {
            return {
                'Private Health Insurance': 'Private Krankenkassen',
                'Statutory Health Insurance': 'Gesetzliche Krankenkassen',
                'Self-Payment': 'Selbstzahlende',
                'Reimbursement Procedure': 'Kostenerstattungsverfahren',
            }[type];
        },
        Vita: 'Erfahrung, Ausbildung, Abschlüsse und Mitgliedschaften',
        Language: 'Sprache',
        Languages: 'Sprachen',
        Profile: 'Profil',
        Gender: 'Gender',
        genderLabel(gender) {
            const labels = { male: 'Männlich', female: 'Weiblich', diverse: 'Divers' };
            return labels[gender];
        },
        searchTherapist: {
            metaTitle(params) {
                return `Psychotherapeuten in ${params.city}: Termin online buchen`;
            },
            metaDescription(params) {
                return `In ${params.city} finden Sie ${params.count === 0 ? '' : params.count} Psychotherapeuten, Psychologen, Ärzte und Heilpraktiker für Psychotherapie. Einfache Online-Terminbuchung.`;
            },
            AreYouATherapistOrCoach: 'Sind Sie Therapeutin oder Coach?',
            HelpSection: 'Hilfebereich',
            YourCity: 'deiner Stadt',
            psychotherapistsInCity(city) {
                return `Psychotherapeuten in ${city}`;
            },
            findAndBookPsychotherapyInCity(city) {
                return `Termine für Psychotherapie in ${city} online finden und buchen`;
            },
            findAvailableTherapySlots(city) {
                return `Freie Therapieplätze und psychologische Beratung in ${city} finden Sie bei Tymia. Gesetzlich Versicherte können als Selbstzahlende oder über das <a href="https://www.tymia.de/psychotherapeuten-finden/kostenerstattungsverfahren-in-der-psychotherapie/">Kostenerstattungsverfahren</a> der gesetzlichen Krankenkassen auch eine Privatpraxis für Psychotherapie in ${city} aufsuchen. Bitte überprüfen Sie vor der Terminbuchung, welche Abrechnungsarten die jeweilige Privatpraxis anbietet. Nach erfolgreicher Terminvereinbarung erhalten Sie eine Buchungsbestätigung per Mail.`;
            },
            resultCount(count) {
                const rule = new Intl.PluralRules('de-DE').select(count);
                if (rule === 'one') {
                    return `${count} Ergebnis gefunden`;
                }
                return `${count} Ergebnisse gefunden`;
            },
            distance(km) {
                return `${km.round()} km`;
            },
            ToProfile: 'Zum Profil',
            YourProfile: 'Ihr Profil?\nAngaben anpassen',
            noAvailableAppointments(type) {
                if (type === 'external') {
                    return 'Keine Online-Terminbuchung über Tymia verfügbar';
                }
                return 'Aktuell keine freien Termine';
            },
            AvailableRemoteAppointment: 'Freier Video-Termin',
            Advertisement: '-Anzeige-',
            filter: {
                label: 'Filter',
                reset: 'Filter zurücksetzen',
                showResult: 'Ergebnisse anzeigen',
                address: {
                    placeholder: 'z. B. München oder 80337',
                },
                location: {
                    label: 'Vor Ort/Video',
                    options: {
                        onSite: 'Vor Ort',
                        remote: 'Video',
                    },
                },
                sort: {
                    label: 'Sortieren nach',
                    options: {
                        distance: 'Entfernung',
                        availability: 'Verfügbarkeit',
                    },
                },
            },
            backLink(city) {
                return `Zurück zu Psychotherapeuten in ${city}`;
            },
        },
        publicProfile: {
            metaDescription(params) {
                return `${params.user} in ${params.city} - Adresse, Kontaktdaten, Terminvereinbarung`;
            },
        },
        export: {
            PatientRecord: 'Patientenakte',
            birthdayLabel(birthday) {
                return `geb. ${birthday}`;
            },
            Diagnoses: 'Diagnosen',
            MainDiagnosis: 'Haupt-Diagnose',
            File: 'Datei',
            MedicalHistory: 'Verlaufsdokumentation',
            MedicalHistoryArchive: 'Archiv Verlaufsdokumentation\n(Bearbeitete oder gelöschte Einträge)',
            Appointments: 'Termine',
            Invoices: 'Rechnungsübersicht',
            InvoiceHeaderLine: 'Rechnungsnumer; Rechnungsdatum; Name, Vorname; Rechnungsbetrag; Status',
            taxedLabel: 'inkl. MwSt.',
        },
    },
    login: {
        tanRequired: 'Bitte geben Sie die PIN ein, die Sie per E-Mail erhalten haben',
        needVerification: 'Account ist noch nicht verifiziert. Dafür kann auch die Funktion "Passwort vergessen" genutzt werden.',
        invalidCredentials: 'E-Mail oder Passwort falsch.',
        invalidTan: 'Die eingegebene PIN ist ungültig.',
        accountLocked: 'Das Profil ist deaktiviert.',
        submit: 'Einloggen',
    },
    forgotPassword: {
        link: 'Passwort vergessen?',
    },
    client: {
        invoice: {
            download: {
                metaTitle: 'Rechnung herunterladen',
                title: {
                    error: 'Fehler',
                    enterTan: 'Identität bestätigen',
                    download: 'Rechnung herunterladen',
                },
                message: {
                    invalidToken: 'Der Download-Link ist ungültig oder abgelaufen.',
                    tokenLocked: 'Download-Link wurde wegen zu vieler falscher TAN-Eingaben dauerhaft gesperrt.',
                    notMobile: 'Die hinterlegte Nummer ist keine gültige Mobilnummer.',
                    enterTan(phone) {
                        return `Bitte geben Sie die TAN ein, die wir per SMS an ${phone} geschickt haben.`;
                    },
                    rateLimited: 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
                    tooManyTansRequested: 'Sie haben bereits mehrere TANs angefordert. Keine weitere TAN möglich.',
                    invalidTan: 'Die eingegebene TAN ist ungültig.',
                },
                input: {
                    tan: {
                        label: 'TAN',
                    },
                    submit: {
                        label: 'Rechnung herunterladen',
                    },
                },
            },
        },
        medicalHistory: {
            stateLabel(state) {
                return {
                    active: 'Aktiv',
                    changed: 'Geändert',
                    deleted: 'Gelöscht',
                }[state];
            },
        },
    },
    appointment: {
        bookingWidget: {
            SelectInsuranceTypeLabel: 'Bitte wählen Sie Ihre Versicherungsart',
            SelectInsuranceTypeSelectEmpty: 'Bitte zuerst Versicherungsart wählen',
            SelectInsuranceTypeSelectSelected: 'Versicherungsart',
            insuranceTypeOption(type) {
                return deDE.user.insuranceTypeLabel(type);
            },
            meetingTypeOption(meetingType) {
                const location = {
                    onSite: 'vor Ort',
                    remote: 'online',
                    both: 'vor Ort und Online',
                }[meetingType.location];
                return `${meetingType.name} ${location} (${meetingType.duration} Minuten)`;
            },
            SelectMeetingTypeLabel: 'Terminart wählen',
            SelectMeetingTypeSelectEmpty: 'Bitte zuerst Terminart wählen',
            SelectMeetingTypeSelectSelected: 'Terminart',
            SelectAppointment: 'Termin auswählen',
            NoAppointments: 'Aktuell keine freien Termine',
            PersonalData: 'Angaben zu Ihrer Person',
            SelectLocationLabel: 'Ort des Termins wählen',
            PrivacyConsent: 'Datenschutzerklärung',
            PrivacyConsentText: 'Meine Daten werden im Auftrag der Praxis durch die Tymia GmbH (Praxissoftware) zur Terminbuchung verarbeitet.',
            PrivacyConsentLabel: 'Ich bin damit einverstanden, dass ich Terminbestätigungen per E-Mail erhalte.',
            BookAppointment: 'Termin buchen',
            BookingSuccessHeader: 'Termin wurde erfolgreich gebucht',
            BookingSuccessMessage: 'Bitte überprüfen Sie Ihr E-Mail Postfach. Alle weiteren Informationen werden Ihnen an die angegebene E-Mail geschickt.',
            bookingSuccessDetails(appointment) {
                return `Terminbeginn: ${deDE.common.dateTime(appointment.appointmentStart)}`;
            },
        },
    },
    invoice: {
        stateLabel(stateDescriptor) {
            switch (stateDescriptor.key) {
                case 'CANCELED':
                    return 'Storniert';
                case 'CANCELED_SENT':
                    return 'Storniert (versandt)';
                case 'PAID':
                    return 'Bezahlt';
                case 'PAID_SENT':
                    return 'Bezahlt (versandt)';
                case 'CREATED':
                    return 'Erstellt';
                case 'SENT':
                    return 'Versandt';
                case 'REMINDER_CREATED':
                    return `${stateDescriptor.number}. Zahlungserinnerung erstellt`;
                case 'REMINDER_SENT':
                    return `${stateDescriptor.number}. Zahlungserinnerung versandt`;
                case 'FINAL_NOTICE_CREATED':
                    return 'Finale Mahnung erstellt';
                case 'FINAL_NOTICE_SENT':
                    return 'Finale Mahnung versandt';
                case 'ERROR':
                    return 'Fehler';
            }
        },
    },
    footer: {
        AboutTymia: 'Über Tymia',
        PrivacyPolicy: 'Datenschutzhinweise',
        Login: 'Login',
        Help: 'Hilfe',
        Imprint: 'Impressum',
    },
    mollie: {
        description(amount) {
            return `${deDE.common.money(amount)} pro Monat, monatlich kündbar`;
        },
    },
};
//# sourceMappingURL=de-DE.js.map